/**
 * QR code for a public form link — generate, download, share, copy, print.
 *
 * Uses the platform's DYNAMIC QR codes (`qr_type = public_form`, TRUS-624): the
 * printed code encodes /qr/<token>, and which link it opens is looked up at each
 * scan. So a flyer keeps working if the link is later replaced — an admin
 * re-points it under Settings > QR Codes — and scans are counted without
 * recording the scanner's IP or location.
 *
 * One code per link: opening this again reuses the code already made for the
 * link rather than minting a second one that would split the scan counts.
 */

import React, { useState } from 'react';
import { QrCode, Download, Share2, Copy, Printer, CheckCircle, Loader2, AlertCircle } from 'lucide-react';
import { apiClient } from '@/services/apiClient';
import { getActiveCboId } from '@/services/api/organizations';
import { useAppTranslation } from '@/contexts/TranslationContext';

interface QRCodeRecord {
  id: string;
  qr_token: string;
  target_url: string;
  qr_image: string; // data:image/png;base64,…
  qr_data?: { public_form_token_id?: number } | null;
}

interface PublicLinkQRCodeProps {
  /** public_form_tokens.id */
  linkId: number;
  /** Shown on the printout and used for the file name. */
  title: string;
}

/** Download size. The API image is small; QR modules scale losslessly. */
const EXPORT_PX = 1024;

function slug(value: string): string {
  return (value || 'form').replace(/[^A-Za-z0-9]+/g, '_').replace(/^_|_$/g, '').slice(0, 60) || 'form';
}

/** Re-draw the QR at print size with nearest-neighbour scaling, so it stays sharp. */
async function toLargePng(dataUrl: string): Promise<Blob> {
  const img = new Image();
  img.src = dataUrl;
  await img.decode();
  const canvas = document.createElement('canvas');
  canvas.width = EXPORT_PX;
  canvas.height = EXPORT_PX;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas unavailable');
  ctx.imageSmoothingEnabled = false;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, EXPORT_PX, EXPORT_PX);
  ctx.drawImage(img, 0, 0, EXPORT_PX, EXPORT_PX);
  return new Promise((resolve, reject) =>
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('png export failed'))), 'image/png'),
  );
}

export default function PublicLinkQRCode({ linkId, title }: PublicLinkQRCodeProps) {
  const { t } = useAppTranslation();
  const [open, setOpen] = useState(false);
  const [qr, setQr] = useState<QRCodeRecord | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const flash = (message: string) => {
    setNotice(message);
    window.setTimeout(() => setNotice(null), 2000);
  };

  const loadOrCreate = async () => {
    const orgId = getActiveCboId();
    if (!orgId) {
      setError(t('public_link_qr.no_org', 'Choose an organization first.'));
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const existing = await apiClient.get<QRCodeRecord[]>('/api/qr-codes', {
        params: { organization_id: orgId, qr_type: 'public_form', is_active: true },
      });
      const match = (existing.data || []).find((q) => q.qr_data?.public_form_token_id === linkId);
      if (match) {
        setQr(match);
        return;
      }
      const created = await apiClient.post<{ qr_code_id: string; qr_token: string; qr_image: string; target_url: string }>(
        '/api/qr-codes',
        {
          organization_id: orgId,
          qr_type: 'public_form',
          owner_type: 'organization',
          owner_id: String(orgId),
          name: title,
          description: t('public_link_qr.description', 'QR code for a public form link'),
          public_form_token_id: linkId,
        },
      );
      setQr({
        id: created.data.qr_code_id,
        qr_token: created.data.qr_token,
        qr_image: created.data.qr_image,
        target_url: created.data.target_url,
        qr_data: { public_form_token_id: linkId },
      });
    } catch {
      setError(t('public_link_qr.failed', 'Could not create the QR code.'));
    } finally {
      setLoading(false);
    }
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !qr && !loading) void loadOrCreate();
  };

  const fileName = `${slug(title)}_qr.png`;

  const download = async () => {
    if (!qr) return;
    try {
      const blob = await toLargePng(qr.qr_image);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError(t('public_link_qr.download_failed', 'Could not download the image.'));
    }
  };

  // Native share sheet (phones, Safari, Edge): the image itself when the browser
  // can share files, otherwise the link; with no share sheet, copy the link.
  const share = async () => {
    if (!qr) return;
    try {
      const blob = await toLargePng(qr.qr_image);
      const file = new File([blob], fileName, { type: 'image/png' });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title, text: qr.target_url });
        return;
      }
      if (navigator.share) {
        await navigator.share({ title, url: qr.target_url });
        return;
      }
      await navigator.clipboard.writeText(qr.target_url);
      flash(t('public_link_qr.link_copied', 'Link copied'));
    } catch (err) {
      // Closing the share sheet rejects with AbortError — that is not a failure.
      if ((err as DOMException)?.name !== 'AbortError') {
        setError(t('public_link_qr.share_failed', 'Could not share from this browser. Use Download instead.'));
      }
    }
  };

  const copyImage = async () => {
    if (!qr) return;
    try {
      const blob = await toLargePng(qr.qr_image);
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
      flash(t('public_link_qr.image_copied', 'Image copied'));
    } catch {
      setError(t('public_link_qr.copy_failed', 'This browser cannot copy images. Use Download instead.'));
    }
  };

  const copyLink = async () => {
    if (!qr) return;
    try {
      await navigator.clipboard.writeText(qr.target_url);
      flash(t('public_link_qr.link_copied', 'Link copied'));
    } catch {
      /* clipboard blocked; the URL is visible below to copy by hand */
    }
  };

  const print = () => {
    if (!qr) return;
    const w = window.open('', '_blank', 'width=600,height=700');
    if (!w) return;
    const safeTitle = title.replace(/[<>&"]/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;' })[c] as string);
    w.document.write(
      `<!doctype html><html><head><title>${safeTitle}</title>` +
        '<style>body{font-family:system-ui,sans-serif;text-align:center;padding:40px}' +
        'img{width:360px;height:360px;image-rendering:pixelated}h1{font-size:20px}p{color:#555;font-size:12px}</style>' +
        `</head><body><h1>${safeTitle}</h1><img src="${qr.qr_image}" alt=""><p>${qr.target_url}</p>` +
        '<script>window.onload=function(){window.print()}</script></body></html>',
    );
    w.document.close();
  };

  const actionClass =
    'inline-flex items-center gap-1 px-2 py-1 text-xs rounded border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200';

  return (
    <div>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        className="flex items-center gap-1 text-xs text-blue-600 hover:underline"
      >
        <QrCode className="w-3 h-3" /> {open ? t('public_link_qr.hide', 'Hide QR code') : t('public_link_qr.show', 'QR code')}
      </button>

      {open && (
        <div className="mt-2 p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          {loading ? (
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" /> {t('public_link_qr.generating', 'Generating QR code…')}
            </div>
          ) : qr ? (
            <div className="flex flex-col sm:flex-row gap-3 items-start">
              <img
                src={qr.qr_image}
                alt={t('public_link_qr.alt', 'QR code for this form')}
                className="w-36 h-36 shrink-0 bg-white rounded border border-gray-100"
                style={{ imageRendering: 'pixelated' }}
              />
              <div className="flex-1 min-w-0 space-y-2">
                <div className="flex flex-wrap gap-1.5">
                  <button type="button" onClick={download} className={actionClass}>
                    <Download className="w-3.5 h-3.5" /> {t('public_link_qr.download', 'Download PNG')}
                  </button>
                  <button type="button" onClick={share} className={actionClass}>
                    <Share2 className="w-3.5 h-3.5" /> {t('public_link_qr.share', 'Share')}
                  </button>
                  <button type="button" onClick={copyImage} className={actionClass}>
                    <Copy className="w-3.5 h-3.5" /> {t('public_link_qr.copy_image', 'Copy image')}
                  </button>
                  <button type="button" onClick={print} className={actionClass}>
                    <Printer className="w-3.5 h-3.5" /> {t('public_link_qr.print', 'Print')}
                  </button>
                </div>
                <button
                  type="button"
                  onClick={copyLink}
                  className="block max-w-full truncate text-left text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400"
                  title={t('public_link_qr.copy_link', 'Copy QR link')}
                >
                  {qr.target_url}
                </button>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t(
                    'public_link_qr.dynamic_help',
                    'This code keeps working if you replace the link later — change where it goes under Settings › QR Codes. Scans are counted without recording location.',
                  )}
                </p>
                {notice && (
                  <p className="flex items-center gap-1 text-xs text-green-600">
                    <CheckCircle className="w-3.5 h-3.5" /> {notice}
                  </p>
                )}
              </div>
            </div>
          ) : null}
          {error && (
            <p className="mt-2 flex items-center gap-1 text-xs text-red-600">
              <AlertCircle className="w-3.5 h-3.5" /> {error}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
